<template>
    <div class="flex flex-col h-full">
        <project-header :project="project" :projects="projects" @project-updated="project = $event" />

        <div class="flex-1 overflow-y-auto">
            <div class="py-5 mx-auto container">
                <a name="project-info"></a>
                <div class="w-full flex flex-col lg:flex-row items-start gap-x-8">
                    <div class="w-1/4 flex flex-col">
                        <div class="flex items-center justify-between">
                            <h2 class="flex items-center text-xl text-gray-700 font-extrabold">
                                Project Details
                                <v-popover v-if="project.description" trigger="hover click" offset="10">
                                    <inline-svg classes="ml-2 mt-2 w-6 h-6 stroke-blue-400 cursor-pointer" name="info"></inline-svg>

                                    <template slot="popover">
                                        <div class="leading-normal max-w-sm p-6">
                                            {{ project.description }}
                                        </div>
                                    </template>
                                </v-popover>
                            </h2>
                            <div v-if="!editing && canEditProject" @click="editing = true" class="cursor-pointer">
                                <inline-svg name="edit-pencil" classes="w-7 h-7"></inline-svg>
                            </div>
                        </div>
                        <table class="mt-2">
                            <tbody>
                                <tr>
                                    <td class="pl-2 font-semibold">Name</td>
                                    <td v-if="! editing">{{ project.name }}</td>
                                    <td v-if="editing">
                                        <input type="text" v-model="project.name" class="ml-3 form-input" />
                                    </td>   
                                </tr>
                                <tr>
                                    <td class="pl-2 font-semibold">Start Date</td>
                                    <td v-if="! editing">{{ project.start_date_formatted }}</td>
                                    <td v-if="editing">
                                        <input type="date" v-model="project.start_date" class="ml-3 form-input" />
                                    </td>
                                </tr>
                                <tr>
                                    <td class="pl-2 font-semibold">End Date</td>
                                    <td v-if="! editing">{{ project.end_date_formatted }}</td>
                                    <td v-if="editing">
                                        <input type="date" v-model="project.end_date" class="ml-3 form-input" />
                                    </td>
                                </tr>
                                <tr>
                                    <td class="pl-2 font-semibold">Usage</td>
                                    <td v-if="! editing">{{ project.property_usage }}</td>
                                    <td v-if="editing">
                                        <select class="ml-3 mt-0 max-w-fit form-select h-full" name="property_usage" v-model="project.property_usage" required>
                                            <option value=""></option>
                                            <option value="Canadian Ops">Canadian Ops</option>
                                            <option value="Corporate HQ">Corporate HQ</option>
                                            <option value="Digital Retailing">Digital Retailing</option>
                                            <option value="DMS">DMS</option>
                                            <option value="Inventory/Interactive">Inventory/Interactive</option>
                                            <option value="Operations">Operations</option>
                                            <option value="Production">Production</option>
                                            <option value="RTS">RTS</option>
                                            <option value="Sales &amp; F&amp;I">Sales &amp; F&amp;I</option>
                                            <option value="Sales RTS">Sales RTS</option>
                                            <option value="Sublet">Sublet</option>
                                            <option value="Technology RTS">Technology RTS</option>
                                            <option value="Vacant">Vacant</option>
                                        </select>
                                    </td>
                                </tr>
                                <tr>
                                    <td class="pl-2 font-semibold">Region</td>
                                    <td v-if="! editing">{{ project.property_region }}</td>
                                    <td v-if="editing">
                                        <select class="ml-3 mt-0 max-w-fit form-select h-full" name="property_region" v-model="project.property_region" required>
                                            <option value=""></option>
                                            <option value="Central">Central</option>
                                            <option value="Mid-Atlantic">Mid-Atlantic</option>
                                            <option value="Northeast">Northeast</option>
                                            <option value="Northwest">Northwest</option>
                                            <option value="Outside USA">Outside USA</option>
                                            <option value="Southeast">Southeast</option>
                                            <option value="Southwest">Southwest</option>
                                        </select>
                                    </td>
                                </tr>
                                <tr>
                                    <td class="pl-2 font-semibold">Current Sq. Ft</td>
                                    <td>{{ project.current_sq_ft | number }}</td>
                                </tr>
                                <tr>
                                    <td class="pl-2 font-semibold">Required Sq. Ft</td>
                                    <td v-if="! editing">{{ project.required_sq_ft | number }}</td>
                                    <td v-if="editing">
                                        <number-input name="required_sq_ft" v-model="project.required_sq_ft" classes="ml-3 text-sm text-right"></number-input>
                                    </td>
                                </tr>
                                <tr>
                                    <td class="pl-2 font-semibold">Property Type</td>
                                    <td v-if="! editing">{{ project.property_type }}</td>
                                    <td v-if="editing">
                                        <select class="ml-3 mt-0 max-w-fit form-select h-full" name="property_type" v-model="project.property_type" required>
                                            <option value=""></option>
                                            <option value="Office">Office</option>
                                            <option value="Industrial">Industrial</option>
                                            <option value="Flex">Flex</option>
                                            <option value="Land">Land</option>
                                            <option value="Retail">Retail</option>
                                        </select>
                                    </td>
                                </tr>
                                <tr>
                                    <td class="pl-2 font-semibold">Project Type:</td>
                                    <td v-if="! editing">{{ project.project_type }}</td>
                                    <td v-if="editing">
                                        <select class="ml-3 mt-0 max-w-fit form-select h-full" name="project_type" v-model="project.project_type" required>
                                            <option value=""></option>
                                            <option value="Consolidation">Consolidation</option>
                                            <option value="Expansion">Expansion</option>
                                            <option value="New Location">New Location</option>
                                            <option value="Renewal">Renewal</option>
                                        </select>
                                    </td>
                                </tr>
                                <tr>
                                    <td class="pl-2 font-semibold">Stage:</td>
                                    <td v-if="! editing">{{ project.stage }}</td>
                                    <td v-if="editing">
                                        <select class="ml-3 mt-0 max-w-fit form-select h-full" name="stage" v-model="project.stage" required>
                                            <option value=""></option>
                                            <option value="Activated Assignment">Activated Assignment</option>
                                            <option value="Approval">Approval</option>
                                            <option value="Build-out">Build-out</option>
                                            <option value="Comps Research">Comps Research</option>
                                            <option value="Final Cut">Final Cut</option>
                                            <option value="Lease Negotiation">Lease Negotiation</option>
                                            <option value="Move In">Move In</option>
                                            <option value="Negotiation">Negotiation</option>
                                            <option value="RFP Process">RFP Process</option>
                                            <option value="Sign Off">Sign Off</option>
                                            <option value="Tour of Comps">Tour of Comps</option>
                                        </select>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <div v-if="editing" class="mt-3 flex items-center justify-between">
                            <button @click.prevent="cancel" type="button" class="btn btn-secondary">
                                Cancel
                            </button>
                            <button @click.prevent="save" type="button" class="btn btn-green">
                                <span v-if="loading" class="flex justify-center">
                                    <inline-svg name="loading-button"></inline-svg>
                                </span>
                                <span v-if="! loading">Save Project</span>
                            </button>
                        </div>
                    </div>

                    <!-- <div class="w-1/4 flex flex-col">
                        <h2 class="text-xl text-gray-700 font-extrabold">KSDs</h2>
                        <div class="mt-2">
                            <ksds
                                :ksdable-id="project.id"
                                ksdable-type="App\Project">
                            </ksds>
                        </div>
                    </div> -->
                    <div class="w-1/4 flex flex-col relative">
                        <div class="absolute -top-1 -right-1">
                            <InfoIcon identifier="inside-a-project-ksds" />
                        </div>

                        <h2 class="text-xl text-gray-700 font-extrabold">KSDs</h2>

                        <div class="mt-2">
                            <ksds
                                :ksdable-id="project.id"
                                ksdable-type="App\Project">
                            </ksds>
                        </div>
                    </div>


                    <div class="w-1/2">
                        <benchmark-chart title="Project Benchmark to Market" height="250" :benchmark="project.benchmark"></benchmark-chart>
                    </div>
                </div>

                <div class="mt-10">
                  <div>
                    <div class="border-b border-gray-200">
                      <nav class="-mb-px flex space-x-2" aria-label="Tabs">
                        <button
                            v-for="(tab, slug) in enabledTabs" :key="slug"
                            @click="selectedTab = slug"
                            class="py-2 px-3 border border-gray-200 flex items-center whitespace-nowrap rounded-t-lg text-sm"
                            :class="{ 'bg-gray-50 text-gray-500 font-medium hover:border-gray-200 hover:text-gray-700': slug != selectedTab, 'bg-gradient-to-br from-green-600 to-green-700 font-bold text-white': slug == selectedTab }">
                                {{ tab.label }}
                            <span
                                v-if="tab.showCount"
                                class="py-0.5 px-2.5 ml-3 hidden rounded-full text-xs font-medium md:inline-block"
                                :class="{ 'bg-gray-200 text-gray-900': slug != selectedTab, 'bg-green-800 text-white': slug == selectedTab }">
                                    {{ tab.count }}
                            </span>
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>

                <div class="px-5 mb-6 border-l border-r border-b">
                    <div class="pt-4">
                        <div v-if="selectedTab == 'comps'">
                            <comp-table
                                :project-id="project.id"
                                :comp-data="project.comps"
                                show-add-comps>
                            </comp-table>
                        </div>

                        <project-site-cards v-if="selectedTab == 'sites'" :site-data="project.sites" view="table">
                            <template slot="action-buttons">
                                <button v-if="canEditProject" @click="$modal.show('project-sites')" class="ml-6 flex items-center btn btn-small btn-indigo">
                                    <inline-svg name="edit-pencil" classes="w-4 h-4 text-white fill-current"></inline-svg>
                                    <span class="ml-2">Manage Sites</span>
                                </button>
                            </template>
                        </project-site-cards>

                        <proposal-cards v-if="selectedTab == 'proposals'" :proposal-data="project.proposals"></proposal-cards>

                        <project-tasks v-if="selectedTab == 'tasks'" :task-data="project.tasks"></project-tasks>

                        <city-stat-cards v-if="selectedTab == 'city_stat'" :city-stat-projects="project.city_stat_projects"></city-stat-cards>

                        <finalist-grid v-if="selectedTab == 'finalist_grid'"></finalist-grid>

                        <key-dates v-if="selectedTab == 'key_dates'" :sites="project.sites" show-site-data></key-dates>

                        <documents
                            v-if="selectedTab == 'documents'"
                            :document-data="project.documents"
                            :documentable-id="project.id"
                            documentable-type="App\Project"
                        ></documents>

                        <contacts
                            v-if="selectedTab == 'contacts'"
                            :contact-data="project.contacts"
                            attach-to="project"
                            :attach-to-id="project.id">
                        </contacts>

                        <comments
                            v-if="selectedTab == 'comments'"
                            :data="project.comments"
                            :document-data="project.documents"
                            :commentable-id="project.id"
                            commentable-type="App\Project"
                        ></comments>

                        <change-log v-if="$gate.isAdmin() && selectedTab == 'changelog'" :data="audits"></change-log>
                    </div>
                </div>

                <notifications classes="notification" position="bottom right"></notifications>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapState } from 'vuex';

    export default {
        name: 'ReoptProject',

        props: {
            'data': {
                type: Object,
                required: true
            },

            'openTab': {
                type: String,
                default: 'comps'
            }
        },

        data() {
            return {
                old: _.cloneDeep(this.data),
                project: this.data,
                editing: false,
                selectedTab: this.openTab,
                loading: false
            }
        },

        mounted() {
            this.projectComps.all = this.project.comps;
            this.projectComps.filtered = this.project.comps;
            this.projects.selectedProject = this.project;

            this.documents.all = this.project.documents;
            this.documents.filtered = this.project.documents;

            this.ksds.scenarios = this.project.ksd_scenarios;
            this.ksds.selectedScenarioId = this.ksds.scenarios[0].id || null;

            this.$store.dispatch('loadProjects');
            this.$store.dispatch('loadSites');
        },

        computed: {
            enabledTabs() {
                // v-for used to be paired with v-if="tab.enabled"; Vue 2 runs
                // v-for first so the list was already filtered. Same result, and
                // it does not break when the directive priority reverses in Vue 3.
                const source = this.tabs;
                return Array.isArray(source)
                    ? source.filter(entry => entry.enabled)
                    : Object.fromEntries(Object.entries(source || {}).filter(([, entry]) => entry.enabled));
            },

            ...mapState(['projects']),
            ...mapState(['documents']),
            ...mapState(['projectComps']),
            
            canEditProject() {
                return this.$gate.can('update', 'project', this.project.id);
            },
            
            canDeleteProject() {
                return this.$gate.can('delete', 'project', this.project.id);
            },
            ...mapState(['ksds']),

            tabs() {
                return {
                    comps: {
                        label: "Comps",
                        enabled: true,
                        showCount: false,
                        //count: this.projectComps.all.length
                    },
                    sites: {
                        label: "Sites",
                        enabled: true,
                        showCount: false,
                        //count: this.project.sites.length
                    },
                    tasks: {
                        label: "Tasks",
                        enabled: true,
                        showCount: false,
                        //count: this.project.tasks.length
                    },
                    city_stat: {
                      label: "CityStat",
                      enabled: false,
                      showCount: false,
                      //count: this.project.city_stat_projects.length
                    },
                    finalist_grid: {
                        label: "Finalist Grid",
                        enabled: true,
                        showCount: false
                    },
                    proposals: {
                        label: "Proposals",
                        enabled: true,
                        showCount: false,
                        //count: this.project.proposals.length
                    },
                    key_dates: {
                        label: "Key Dates",
                        enabled: true,
                        showCount: false,
                        //count: this.project.key_dates_count
                    },
                    documents: {
                        label: "Documents",
                        enabled: true,
                        showCount: false,
                        //count: this.documents.all.length || 0
                    },
                    contacts: {
                        label: "Contacts",
                        enabled: true,
                        showCount: false,
                        //count: this.project.contacts.length
                    },
                    comments: {
                        label: "Comments",
                        enabled: true,
                        showCount: false,
                        //count: this.project.comments.length
                    },
                    changelog: {
                        label: "Changelog",
                        enabled: this.$gate.isAdmin(),
                        showCount: false,
                        //count: this.project.audits.length
                    }
                }
            },

            audits() {
                return _.orderBy(this.project.audits, 'created_at', 'desc');
            }
        },

        methods: {
            cancel() {
                this.project = _.cloneDeep(this.old);
                this.editing = false;
            },

            save() {
                this.loading = true;

                axios.patch('/projects/' + this.project.id, this.project).then(response => {
                    this.old = response.data.project;
                    this.project = response.data.project;
                    this.projects.selectedProject = response.data.project;

                    this.editing = false;

                    this.$notify({
                        type: 'success',
                        title: 'Success',
                        text: response.data.message
                    })
                }).catch(error => {
                    this.$notify({
                        type: 'error',
                        title: 'Error',
                        text: error.response.data.message
                    });
                }).finally(() => {
                    this.loading = false;
                });
            },

            confirmDeleteProject() {
                this.$swal({
                    title: 'Are you sure?',
                    text: "Are you sure you want to delete this project?",
                    type: 'warning',
                    buttonsStyling: false,
                    confirmButtonText: 'Yes - Delete it',
                    confirmButtonClass: 'btn btn-red',
                    showCancelButton: true,
                    cancelButtonClass: 'btn btn-secondary mr-4',
                    reverseButtons: true
                }).then((result) => {
                    if (result.value) {
                        this.deleteProject();
                    }
                });
            },

            deleteProject() {
                axios.delete('/projects/' + this.project.id)
                    .then(response => {
                        this.$notify({
                            type: 'success',
                            title: 'Success',
                            text: 'Project Deleted'
                        });

                        setTimeout(() => {
                            window.location = '/projects'
                        }, 1000);
                    })
                    .catch(error => {
                        this.$notify({
                            type: 'error',
                            title: 'Error',
                            text: error.response.data.message
                        });
                    }
                );
            }
        }
    }
</script>
